<template>
  <v-row no-gutters>
    <v-col
      class="pa-6"
    >
      <v-row
        dense
      >
        <v-col>
          <validation-provider
            v-slot="{ errors }"
          >
            <number-input
              v-model="dirtySetParams.NMRP"
              outlined
              dense
              :label="$t('training_builder.text_input.no_of_repetitions.title')"
              :placeholder="$t('label.e_g') + ' ' + 10"
              :decimal-precision="0"
              :error-messages="errors"
            />
          </validation-provider>
        </v-col>
        <v-col>
          <minute-second-input
            :value="dirtySetParams.WRPRDR"
            :label="$t('training_builder.text_input.working_period.title')"
            variable="WRPRDR"
            @input="handleDurationInput"
          />
        </v-col>
        <v-col>
          <minute-second-input
            :value="dirtySetParams.RSPRDR"
            :label="$t('training_builder.text_input.resting_period.title')"
            variable="RSPRDR"
            @input="handleDurationInput"
          />
        </v-col>
      </v-row>

      <v-divider class="mb-6" />

      <v-row dense>
        <v-col cols="6">
          <minute-second-input
            :value="dirtySetParams.RSSTDR"
            :label="$t('training_builder.text_input.rest_time_after_set')"
            variable="RSSTDR"
            @input="handleDurationInput"
          />
        </v-col>
      </v-row>

      <v-divider class="mb-6" />

      <v-row dense>
        <v-col>
          <validation-provider
            v-slot="{ errors }"
          >
            <number-input
              v-model="dirtySetParams.NMST"
              outlined
              :label="$t('training_builder.text_input.no_of_stations.title')"
              :placeholder="$t('label.e_g') + ' ' + 5"
              dense
              :decimal-precision="0"
              :error-messages="errors"
            />
          </validation-provider>
        </v-col>
        <v-col>
          <validation-provider
            v-slot="{ errors }"
          >
            <number-input
              v-model="dirtySetParams.PLST"
              outlined
              dense
              :label="$t('training_builder.text_input.players_on_station.title')"
              :placeholder="$t('label.e_g') + ' ' + 10"
              :decimal-precision="0"
              :error-messages="errors"
            />
          </validation-provider>
        </v-col>
        <v-col>
          <validation-provider
            v-slot="{ errors }"
          >
            <number-input
              :value="displayedDMS1"
              outlined
              dense
              :label="$t('training_builder.text_input.width.title')"
              :placeholder="$t('label.e_g') + ' ' + 20"
              :suffix="distanceSuffix(user.organisation_settings.unit_system)"
              :decimal-precision="1"
              :error-messages="errors"
              @input="displayedDMS1 = $event"
            />
          </validation-provider>
        </v-col>
        <v-col>
          <validation-provider
            v-slot="{ errors }"
          >
            <number-input
              :value="displayedDMS2"
              outlined
              dense
              :label="$t('training_builder.text_input.height.title')"
              :placeholder="$t('label.e_g') + ' ' + 20"
              :suffix="distanceSuffix(user.organisation_settings.unit_system)"
              :decimal-precision="1"
              :error-messages="errors"
              @input="displayedDMS2 = $event"
            />
          </validation-provider>
        </v-col>
      </v-row>

      <v-divider class="mb-6" />

      <v-row dense>
        <v-col cols="3">
          <div class="text-body-1 mb-2">
            {{ $t('training_builder.dropdown.terrain.title') }}
          </div>
          <validation-provider
            v-slot="{ errors }"
          >
            <v-select
              v-model="dirtySetParams.TRRN"
              outlined
              dense
              :placeholder="$t('training_builder.dropdown.terrain.placeholder')"
              :error-messages="errors"
              :items="[ { text: $t('training_builder.dropdown.terrain.item.grass'), value: 'Grass' }, { text: $t('training_builder.dropdown.terrain.item.artificial_grass'), value: 'Artificial grass' }, { text: $t('training_builder.dropdown.terrain.item.hybrid_grass'), value: 'Hybrid grass' }, { text: $t('training_builder.dropdown.terrain.item.parquet'), value: 'Parquet' }, { text: $t('training_builder.dropdown.terrain.item.concrete'), value: 'Concrete' }, { text: $t('training_builder.dropdown.terrain.item.sand'), value: 'Sand' }, ]"
            />
          </validation-provider>
        </v-col>
        <v-col cols="3">
          <div class="text-body-1 mb-2">
            {{ $t('training_builder.dropdown.goal.title') }}
          </div>
          <validation-provider
            v-slot="{ errors }"
          >
            <v-select
              v-model="dirtySetParams.GOAL"
              outlined
              dense
              :placeholder="$t('training_builder.dropdown.goal.placeholder')"
              :error-messages="errors"
              :items="[ { text: $t('training_builder.dropdown.goal.item.no_goal'), value: 'No goal' }, { text: $t('training_builder.dropdown.goal.item.big_goal'), value: 'Big goal' }, { text: $t('training_builder.dropdown.goal.item.small_goal'), value: 'Small goal' } ]"
            />
          </validation-provider>
        </v-col>
        <v-col cols="3">
          <validation-provider
            v-slot="{ errors }"
          >
            <number-input
              v-model="dirtySetParams.NMCNT"
              outlined
              :label="$t('training_builder.text_input.no_of_contacts.title')"
              :placeholder="$t('label.e_g') + ' ' + 25"
              dense
              :decimal-precision="0"
              :error-messages="errors"
            />
          </validation-provider>
        </v-col>
      </v-row>

      <v-divider class="mb-6" />

      <div class="text-body-1">
        {{ $t('training_builder.slider.effort_level.title') }}
      </div>
      <v-btn-toggle
        v-model="dirtySetParams.EFFRT"
        color="primary"
        class="mt-2 v-btn-toggle--block"
      >
        <v-btn
          v-for="n in 10"
          :key="n"
          :value="n"
          small
        >
          {{ n }}
        </v-btn>
      </v-btn-toggle>

      <v-divider
        v-if="exercise.drill && user.organisation_settings && user.organisation_settings.has_drill_module && teamWithDrill"
        class="mb-6"
      />

      <v-card
        v-if="exercise.drill && user.organisation_settings && user.organisation_settings.has_drill_module && teamWithDrill"
        outlined
        class="mb-6 elevation-0 grey lighten-4"
      >
        <v-row>
          <v-col>
            <v-img
              :src="exercise.drill.preview_url"
              class="ma-3"
              width="200"
              contain
            />
          </v-col>
          <v-col>
            <v-row class="flex-column mt-2">
              <v-col>
                <span class="font-weight-bold">
                  {{ exercise.drill.name }}
                </span>
              </v-col>
              <v-col>
                <div
                  class="caption mb-3"
                  style="-webkit-line-clamp: 4; -webkit-box-orient: vertical; display: -webkit-box; overflow: hidden; width: 100%"
                >
                  {{ exercise.drill.description }}
                </div>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="auto">
            <v-btn
              text
              small
              :loading="loading"
              color="primary"
              class="text-none mr-2 mt-2"
              @click="openEditDrillDialog()"
            >
              <v-icon
                left
                small
              >
                far fa-pen
              </v-icon>
              Edit for this protocol
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <card-dialog
        v-if="exercise.drill"
        :key="'Edit drill' + componentKey"
        v-model="editDrill"
        :title="$t('card_dialog.edit_drill.title')"
        :scrollable="false"
        fullscreen
        persistent
      >
        <v-card-text>
          <div
            style="height: 2px"
          >
            <v-progress-linear
              v-if="submitting"
              indeterminate
              color="primary"
              :height="2"
            />
          </div>
          <HomeView
            :drill="exercise.drill"
            :sport="currentTeam.sport.name"
            :players="drillPlayers.data"
            :teams="teams"
            :user="user"
            @save="handleDrillEdit"
          />
        </v-card-text>
      </card-dialog>
    </v-col>

    <v-col
      v-if="players && players.length > 0"
      cols="auto"
      class="grey lighten-5 pa-6"
      style="border-left: 1px solid #e0e0e0; width: 590px; flex: 0 0 590px"
    >
      <v-row
        no-gutters
        align="center"
        class="mb-3"
      >
        <v-col>
          <subtitle>
            {{ $t('training_builder.player_groups_teams.title') }}
          </subtitle>
        </v-col>
        <v-col cols="auto">
          <v-btn
            text
            color="primary"
            class="text-none"
            @click="addGroup()"
          >
            <v-icon
              left
              small
            >
              fal fa-plus
            </v-icon>
            {{ $t('training_builder.button.add_group') }}
          </v-btn>
        </v-col>
      </v-row>

      <v-card
        v-for="(group, groupIndex) in (dirtySetParams.groups || [])"
        :key="'group' + groupIndex"
        outlined
        class="mb-4 pa-4"
      >
        <v-row
          no-gutters
          align="center"
          class="mb-4"
        >
          <v-col>
            <div class="text-body-2 font-weight-medium">
              {{ groupName(group, groupIndex) }}
            </div>
          </v-col>
          <v-col cols="auto">
            <v-btn-toggle
              v-model="group.type"
              mandatory
              dense
              small
              class="mr-4"
              @change="val => handleGroupTypeChange(group, val)"
            >
              <v-btn
                v-for="item in groupTypeItems"
                :key="item.value"
                :value="item.value"
                small
              >
                {{ item.text }}
              </v-btn>
            </v-btn-toggle>
            <v-btn
              icon
              small
              color="error"
              @click="removeGroup(groupIndex)"
            >
              <v-icon small>
                fal fa-trash
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-autocomplete
              v-model="group.player_ids"
              :items="players"
              :item-text="p => p.first_name + ' ' + p.last_name"
              item-value="id"
              multiple
              outlined
              clearable
              dense
              hide-details
              small-chips
              deletable-chips
              :label="$t('dropdown.players.title')"
              @change="val => handlePlayersChange(group, val)"
            >
              <template v-slot:item="{ item }">
                <v-list-item-action>
                  <v-simple-checkbox
                    :value="group.player_ids.includes(item.id)"
                    readonly
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ item.first_name }} {{ item.last_name }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>

        <v-list
          v-if="group.player_ids && group.player_ids.length > 0"
          class="pa-0 mt-2 v-list--bordered"
        >
          <template v-for="(playerId, pIndex) in group.player_ids">
            <v-list-item
              :key="'player' + playerId"
              class="px-0"
            >
              <v-list-item-avatar
                v-if="getPlayerPhoto(playerId, group)"
                size="32"
              >
                <v-img :src="getPlayerPhoto(playerId, group)" />
              </v-list-item-avatar>
              <v-list-item-avatar
                v-else
                size="32"
                color="grey lighten-2"
              >
                <v-icon small>
                  fal fa-user
                </v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ getPlayerName(playerId, group) }}
                </v-list-item-title>
              </v-list-item-content>

              <v-list-item-action class="flex-row align-center">
                <v-btn-toggle
                  v-if="group.type === 'team'"
                  :value="isPlayerInsideJoker(group, playerId) ? 'inside' : (isPlayerOutsideJoker(group, playerId) ? 'outside' : undefined)"
                  dense
                  x-small
                  class="mr-4"
                  @change="val => toggleJokerType(group, playerId, val)"
                >
                  <v-btn
                    value="inside"
                    x-small
                  >
                    {{ $t('label.inside') }}
                  </v-btn>
                  <v-btn
                    value="outside"
                    x-small
                  >
                    {{ $t('label.outside') }}
                  </v-btn>
                </v-btn-toggle>
                <v-btn-toggle
                  v-else
                  :value="group.type === 'inside_jokers' ? 'inside' : 'outside'"
                  dense
                  x-small
                  disabled
                  class="mr-4"
                >
                  <v-btn
                    value="inside"
                    x-small
                    disabled
                  >
                    {{ $t('label.inside') }}
                  </v-btn>
                  <v-btn
                    value="outside"
                    x-small
                    disabled
                  >
                    {{ $t('label.outside') }}
                  </v-btn>
                </v-btn-toggle>

                <v-btn
                  icon
                  small
                  @click="removePlayer(group, pIndex)"
                >
                  <v-icon small>
                    fal fa-times
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import NumberInput from '@/components/helpers/NumberInput.vue'
import { cloneVariables, convertDistanceByUnitSystem, distanceSuffix } from '@/components/training-builder/helpers'
import MinuteSecondInput from '@/components/helpers/MinuteSecondInput.vue'
import CardDialog from '@/components/helpers/CardDialog.vue'
import Subtitle from '@/components/helpers/Subtitle.vue'
import { PlayerApiService } from '@/data/services'
import { DrillApiService } from '@/data/services/DrillApiService'
import { EventBus } from '@/plugins/bus'
import { cloneDeep } from 'lodash'

export default {
  name: 'FootballParamsEntry',
  components: { Subtitle, CardDialog, MinuteSecondInput, NumberInput, HomeView: () => import('@ultrax-ai/drill-package') },
  props: {
    exercise: {
      type: Object,
      required: true
    },
    setParams: {
      type: Object,
      required: true
    },
    players: {
      type: Array,
      default: () => ([])
    }
  },
  data() {
    return {
      dirtySetParams: cloneVariables(this.setParams),
      editDrill: false,
      drillPlayers: {
        data: [],
        loading: false
      },
      submitting: false,
      loading: false,
      componentKey: 1,
      internalDMS1InMeters: null,
      internalDMS2InMeters: null
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    displayedDMS1: {
      get() {
        return convertDistanceByUnitSystem(this.internalDMS1InMeters, this.user.organisation_settings.unit_system, true)
      },
      set(value) {
        this.dirtySetParams.DMS1 = convertDistanceByUnitSystem(value, this.user.organisation_settings.unit_system, false)
      }
    },
    displayedDMS2: {
      get() {
        return convertDistanceByUnitSystem(this.internalDMS2InMeters, this.user.organisation_settings.unit_system, true)
      },
      set(value) {
        this.dirtySetParams.DMS2 = convertDistanceByUnitSystem(value, this.user.organisation_settings.unit_system, false)
      }
    },
    teamWithDrill() {
      return ['Football', 'Futsal', 'Handball', 'Basketball', 'Hockey', 'US Football', 'Water Polo', 'Volleyball', 'Badminton', 'Baseball', 'Lacrosse', 'Pickleball', 'Tennis', 'Field Hockey'].includes(this.currentTeam?.sport?.name) || false
    },
    groupTypeItems() {
      return [
        { text: this.$t('dropdown.group_type.team'), value: 'team' },
        { text: this.$t('dropdown.group_type.inside_jokers'), value: 'inside_jokers' },
        { text: this.$t('dropdown.group_type.outside_jokers'), value: 'outside_jokers' }
      ]
    }
  },
  watch: {
    dirtySetParams: {
      deep: true,
      handler: function (newValue) {
        this.$emit('update:setParams', newValue)
      }
    },
    editDrill: function (val, oldVal) {
      if (val) this.componentKey++
      else if (oldVal) {
        this.$nextTick(() => {
          this.componentKey++
        })
      }
    }
  },
  mounted() {
    this.fetchDrillPlayers()

    this.internalDMS1InMeters = cloneDeep(this.dirtySetParams.DMS1)
    this.internalDMS2InMeters = cloneDeep(this.dirtySetParams.DMS2)
  },
  methods: {
    distanceSuffix,
    checkIfParamExists(param) {
      if (!this.exercise.tags) return false

      return this.exercise.tags.some(t => t.tag_id === 'params' && t.value === param)
    },
    handleDurationInput(min, sec, variable) {
      this.dirtySetParams[variable] = min * 60 + sec > 0 ? min * 60 + sec : null
    },
    async fetchDrillPlayers() {
      try {
        this.drillPlayers.loading = true

        const resp = await PlayerApiService.getSimpleList({})

        this.drillPlayers.data = resp.data
      } catch (e) {
        this.drillPlayers.data = []
      } finally {
        this.drillPlayers.loading = false
      }
    },
    async handleDrillEdit(saveData) {
      try {
        this.submitting = true

        const data = {
          name: saveData.name,
          description: saveData.description,
          preview: saveData.preview,
          state: saveData.state,
          coach_id: this.user.coach.id,
          team_ids: saveData.team_ids,
          player_ids: saveData.player_ids,
          is_hidden: true,
          tags: saveData.tags
        }

        const result = await DrillApiService.create(data)

        this.exercise.drill = result.data
        this.editDrill = false

        EventBus.$emit(this.$events.DRILL_CREATED)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async openEditDrillDialog() {
      try {
        this.loading = true
        const stateResponse = await fetch(this.exercise.drill.state_url)
        this.exercise.drill.state = await stateResponse.json()

        this.editDrill = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_loading.error'),
          color: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    addGroup() {
      if (!this.dirtySetParams.groups) {
        this.$set(this.dirtySetParams, 'groups', [])
      }

      this.dirtySetParams.groups.push({
        type: 'team',
        player_ids: [],
        inside_joker_ids: [],
        outside_joker_ids: []
      })
    },
    removeGroup(index) {
      this.dirtySetParams.groups.splice(index, 1)
    },
    handlePlayersChange(group, playerIds) {
      if (group.type === 'inside_jokers') {
        playerIds.forEach(playerId => {
          this.toggleJokerType(group, playerId, 'inside')
        })
      } else if (group.type === 'outside_jokers') {
        playerIds.forEach(playerId => {
          this.toggleJokerType(group, playerId, 'outside')
        })
      }
    },
    handleGroupTypeChange(group, type) {
      if (type === 'team') {
        this.$set(group, 'inside_joker_ids', [])
        this.$set(group, 'outside_joker_ids', [])
      } else if (type === 'inside_jokers') {
        this.$set(group, 'outside_joker_ids', [])
        this.$set(group, 'inside_joker_ids', [...group.player_ids])
      } else if (type === 'outside_jokers') {
        this.$set(group, 'inside_joker_ids', [])
        this.$set(group, 'outside_joker_ids', [...group.player_ids])
      }
    },
    removePlayer(group, pIndex) {
      const playerId = group.player_ids[pIndex]
      group.player_ids.splice(pIndex, 1)
      this.toggleJokerType(group, playerId, undefined)
    },
    groupName(group, index) {
      if (group.type === 'outside_jokers') return this.$t('dropdown.group_type.outside_jokers')
      if (group.type === 'inside_jokers') return this.$t('dropdown.group_type.inside_jokers')

      const teamIndex = (this.dirtySetParams.groups || [])
        .filter((g, i) => g.type === 'team' && i <= index)
        .length

      return this.$t('label.team') + ' ' + teamIndex
    },
    isPlayerInsideJoker(group, playerId) {
      if (group.type === 'inside_jokers') return true
      return (group.inside_joker_ids || []).includes(playerId)
    },
    isPlayerOutsideJoker(group, playerId) {
      if (group.type === 'outside_jokers') return true
      return (group.outside_joker_ids || []).includes(playerId)
    },
    toggleJokerType(group, playerId, type) {
      if (!group.inside_joker_ids) this.$set(group, 'inside_joker_ids', [])
      if (!group.outside_joker_ids) this.$set(group, 'outside_joker_ids', [])

      const insideIndex = group.inside_joker_ids.indexOf(playerId)
      if (insideIndex > -1) group.inside_joker_ids.splice(insideIndex, 1)

      const outsideIndex = group.outside_joker_ids.indexOf(playerId)
      if (outsideIndex > -1) group.outside_joker_ids.splice(outsideIndex, 1)

      if (type === 'inside') {
        if (!group.inside_joker_ids.includes(playerId)) group.inside_joker_ids.push(playerId)
      } else if (type === 'outside') {
        if (!group.outside_joker_ids.includes(playerId)) group.outside_joker_ids.push(playerId)
      }
    },
    getPlayerName(playerId, group) {
      const player = this.players.find(p => p.id === playerId)
      if (player) return `${player.first_name} ${player.last_name}`

      const nameData = group?.player_names?.[playerId]
      if (nameData) return `${nameData.first_name} ${nameData.last_name}`

      return ''
    },
    getPlayer(playerId) {
      return this.players.find(p => p.id === playerId)
    },
    getPlayerPhoto(playerId, group) {
      const player = this.players.find(p => p.id === playerId)
      if (player?.photo_url) return player.photo_url

      return group?.player_names?.[playerId]?.photo_url || null
    }
  }
}
</script>
